/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Search, Filter, FilterX } from 'lucide-react';

interface UserStoryFiltersProps {
  searchTerm: string;
  statusFilter: string;
  sectionFilter: string;
  epicFilter: string;
  onSearchChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onSectionFilterChange: (value: string) => void;
  onEpicFilterChange: (value: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  statusOptions: string[];
  sections: string[];
  epics: string[];
}

export function UserStoryFilters({
  searchTerm,
  statusFilter,
  sectionFilter,
  epicFilter,
  onSearchChange,
  onStatusFilterChange,
  onSectionFilterChange,
  onEpicFilterChange,
  onClearFilters,
  hasActiveFilters,
  statusOptions,
  sections,
  epics
}: UserStoryFiltersProps) {
  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      background: '#f8f9fa'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Search Input */}
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6c757d',
            pointerEvents: 'none'
          }} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by title, role, goal, benefit, or acceptance criteria..."
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Filter Dropdowns */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Filter size={14} style={{ color: '#6c757d' }} />
            <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>Filters:</span>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Statuses</option>
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          {/* Section Filter */}
          <select
            value={sectionFilter}
            onChange={(e) => onSectionFilterChange(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Sections</option>
            {sections.map(section => (
              <option key={section} value={section}>{section}</option>
            ))}
          </select>

          {/* Epic Filter */}
          <select
            value={epicFilter}
            onChange={(e) => onEpicFilterChange(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Epics</option>
            {epics.map(epic => (
              <option key={epic} value={epic}>{epic}</option>
            ))}
          </select>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="btn btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                fontSize: '14px',
                marginLeft: 'auto'
              }}
              title="Clear all filters and search"
            >
              <FilterX size={14} />
              Clear Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

